import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { restoreCart } from '../utils/cartPersistence';

// Cart Item Interface
export interface CartItem {
  marketId?: string; // Optional for one-time products
  marketName?: string; // Optional for one-time products
  priceId: string;
  price: number;
  productId: string;
  productName: string; // Add product name for display
  interval?: 'monthly' | 'yearly'; // Optional for one-time products
  productType: 'subscription' | 'one_time' | 'single_purchase'; // Distinguish between product types
  customAmount?: number; // Custom price in dollars for eligible users (single_purchase only)
  reportType?: 'compset' | 'submarket'; // Type of report for one-time purchases
}

// Cart Context Type
interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => { success: boolean; error?: string };
  removeItem: (priceId: string) => void;
  clearCart: () => void;
  totalPrice: number;
  itemCount: number;
  isItemInCart: (priceId: string) => boolean;
  canAddToCart: () => boolean;
  maxItems: number;
  getCartInterval: () => 'monthly' | 'yearly' | null;
  hasOneTimeProducts: () => boolean;
  hasSubscriptions: () => boolean;
  hasSinglePurchases: () => boolean;
  onIntervalMismatch?: (cartInterval: string, newInterval: string) => void;
  onProductTypeMismatch?: (message: string) => void;
}

// Create Context
const CartContext = createContext<CartContextType | undefined>(undefined);

// Local Storage Key
const CART_STORAGE_KEY = 'horwath-cart';

// Cart Limits
const MAX_CART_ITEMS = 10;

// Migration function for existing cart data
const migrateCartItems = (items: any[]): CartItem[] => {
  return items.map(item => ({
    ...item,
    productName: item.productName || item.marketName || 'Unknown Product',
    productType: item.productType || 'subscription',
    interval: item.interval || 
      (item.productId?.includes('monthly') ? 'monthly' : 'yearly')
  }));
};

// Function to initialize cart from localStorage
const initializeCart = (): CartItem[] => {
  try {
    // First attempt to restore cart from backups (if needed)
    restoreCart();
    
    // Now load from localStorage
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    
    if (savedCart) {
      const parsedCart = JSON.parse(savedCart);
      if (Array.isArray(parsedCart)) {
        // Migrate cart items to include interval if missing
        const migratedCart = migrateCartItems(parsedCart);
        return migratedCart;
      }
    }
    return [];
  } catch (error) {
    console.error('🛒 CartProvider: Error loading cart from localStorage:', error);
    return [];
  }
};

// Cart Provider Component
export const CartProvider: React.FC<{
  children: ReactNode;
  onIntervalMismatch?: (cartInterval: string, newInterval: string) => void;
  onProductTypeMismatch?: (message: string) => void;
}> = ({ children, onIntervalMismatch, onProductTypeMismatch }) => {
  // Initialize state with cart from localStorage
  const [items, setItems] = useState<CartItem[]>(initializeCart);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [items]);

  // Add item to cart
  const addItem = (item: CartItem): { success: boolean; error?: string } => {
    let result = { success: true, error: undefined as string | undefined };

    setItems(prevItems => {
      // Check if item already exists (prevent duplicates by priceId)
      const existingItem = prevItems.find(i => i.priceId === item.priceId);
      if (existingItem) {
        result = { success: false, error: 'Item already in cart' };
        return prevItems;
      }

      // Check for product type mixing
      if (prevItems.length > 0) {
        const existingSubscriptions = prevItems.filter(i => i.productType === 'subscription');
        const existingOneTimeProducts = prevItems.filter(i => i.productType === 'one_time');
        const existingSinglePurchases = prevItems.filter(i => i.productType === 'single_purchase');

        // Rule 1 & 2: Check subscription interval mixing
        if (item.productType === 'subscription' && existingSubscriptions.length > 0) {
          const cartInterval = existingSubscriptions[0].interval;
          if (item.interval !== cartInterval) {
            // Different subscription intervals not allowed
            const message = `Cannot mix ${item.interval} subscriptions with ${cartInterval} subscriptions. Please clear your cart first.`;
            result = { success: false, error: message };
            if (onProductTypeMismatch) {
              onProductTypeMismatch(message);
            } else if (onIntervalMismatch) {
              // Fallback to old callback for backward compatibility
              onIntervalMismatch(cartInterval!, item.interval!);
            }
            return prevItems; // Don't add the item
          }
        }

        // Rule 3: Check if adding subscription to cart with one-time products
        if (item.productType === 'subscription' && existingOneTimeProducts.length > 0) {
          const message = 'Cannot mix subscription products with one-time purchase products. Please clear your cart first.';
          result = { success: false, error: message };
          if (onProductTypeMismatch) {
            onProductTypeMismatch(message);
          }
          return prevItems; // Don't add the item
        }

        // Rule 3: Check if adding one-time product to cart with subscriptions
        if (item.productType === 'one_time' && existingSubscriptions.length > 0) {
          const interval = existingSubscriptions[0].interval;
          const message = `Cannot mix one-time purchase products with ${interval} subscription products. Please clear your cart first.`;
          result = { success: false, error: message };
          if (onProductTypeMismatch) {
            onProductTypeMismatch(message);
          }
          return prevItems; // Don't add the item
        }

        // Rule 4: Check if adding single_purchase to cart with subscriptions
        if (item.productType === 'single_purchase' && existingSubscriptions.length > 0) {
          const message = 'Cannot mix 30-day access purchases with subscription products. Please clear your cart first.';
          result = { success: false, error: message };
          if (onProductTypeMismatch) {
            onProductTypeMismatch(message);
          }
          return prevItems; // Don't add the item
        }

        // Rule 5: Check if adding subscription to cart with single_purchases
        if (item.productType === 'subscription' && existingSinglePurchases.length > 0) {
          const message = 'Cannot mix subscription products with 30-day access purchases. Please clear your cart first.';
          result = { success: false, error: message };
          if (onProductTypeMismatch) {
            onProductTypeMismatch(message);
          }
          return prevItems; // Don't add the item
        }

        // Rule 6: Check if adding single_purchase to cart with one-time products
        if (item.productType === 'single_purchase' && existingOneTimeProducts.length > 0) {
          const message = 'Cannot mix 30-day access purchases with one-time purchase products. Please clear your cart first.';
          result = { success: false, error: message };
          if (onProductTypeMismatch) {
            onProductTypeMismatch(message);
          }
          return prevItems; // Don't add the item
        }

        // Rule 7: Check if adding one-time product to cart with single_purchases
        if (item.productType === 'one_time' && existingSinglePurchases.length > 0) {
          const message = 'Cannot mix one-time purchase products with 30-day access purchases. Please clear your cart first.';
          result = { success: false, error: message };
          if (onProductTypeMismatch) {
            onProductTypeMismatch(message);
          }
          return prevItems; // Don't add the item
        }
      }

      // Check cart limit
      if (prevItems.length >= MAX_CART_ITEMS) {
        const message = `Cart is full. Maximum ${MAX_CART_ITEMS} items allowed.`;
        result = { success: false, error: message };
        if (onProductTypeMismatch) {
          onProductTypeMismatch(message);
        }
        return prevItems;
      }

      // Validate price is positive (except for single_purchase with custom pricing which can be $0)
      if (item.price < 0 || (item.price === 0 && item.productType !== 'single_purchase')) {
        result = { success: false, error: 'Invalid price: Price must be greater than 0' };
        console.error('Invalid price: Price must be greater than 0');
        return prevItems;
      }

      // Add new item
      result = { success: true, error: undefined };
      return [...prevItems, item];
    });

    return result;
  };

  // Remove item from cart (by priceId for uniqueness)
  const removeItem = (priceId: string) => {
    setItems(prevItems => prevItems.filter(item => item.priceId !== priceId));
  };

  // Clear entire cart
  const clearCart = () => {
    setItems([]);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing cart from localStorage:', error);
    }
  };

  // Calculate total price
  const totalPrice = items.reduce((sum, item) => sum + item.price, 0);

  // Get item count
  const itemCount = items.length;

  // Check if item is in cart (by priceId)
  const isItemInCart = (priceId: string): boolean => {
    return items.some(item => item.priceId === priceId);
  };

  // Check if we can add more items to cart
  const canAddToCart = (): boolean => {
    return items.length < MAX_CART_ITEMS;
  };

  // Get the current cart interval (for subscriptions)
  const getCartInterval = (): 'monthly' | 'yearly' | null => {
    const subscriptions = items.filter(item => item.productType === 'subscription');
    if (subscriptions.length === 0) return null;
    return subscriptions[0].interval || null;
  };

  // Check if cart has one-time products
  const hasOneTimeProducts = (): boolean => {
    return items.some(item => item.productType === 'one_time');
  };

  // Check if cart has subscriptions
  const hasSubscriptions = (): boolean => {
    return items.some(item => item.productType === 'subscription');
  };

  // Check if cart has single purchases (30-day access)
  const hasSinglePurchases = (): boolean => {
    return items.some(item => item.productType === 'single_purchase');
  };

  const value: CartContextType = {
    items,
    addItem,
    removeItem,
    clearCart,
    totalPrice,
    itemCount,
    isItemInCart,
    canAddToCart,
    maxItems: MAX_CART_ITEMS,
    getCartInterval,
    hasOneTimeProducts,
    hasSubscriptions,
    hasSinglePurchases,
    onIntervalMismatch,
    onProductTypeMismatch,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Helper function to format price
export const formatPrice = (price: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(price / 100); // Convert from cents to dollars
};